import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  RefreshCw, 
  Trash2, 
  Database, 
  Clock, 
  Zap, 
  AlertCircle,
  TrendingUp,
  Activity
} from "lucide-react";
import { cacheUtils, cachePerformance, backgroundSync } from "@/lib/cache";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Only show in development
const CacheManager = () => {
  const queryClient = useQueryClient();
  const [stats, setStats] = useState<any>(null);
  const [perfStats, setPerfStats] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Update stats every 2 seconds when dialog is open
  useEffect(() => {
    if (!isOpen) return;
    
    const updateStats = () => {
      setStats(cacheUtils.getCacheStats(queryClient));
      setPerfStats(cachePerformance.getStats());
    };

    updateStats(); // Initial load
    const interval = setInterval(updateStats, 2000);
    
    return () => clearInterval(interval);
  }, [queryClient, isOpen]);

  // Don't render in production
  if (!import.meta.env.DEV) return null;

  const handleClearAll = () => {
    cacheUtils.clearAll(queryClient);
    setStats(cacheUtils.getCacheStats(queryClient));
    setPerfStats(cachePerformance.getStats());
  };

  const handleForceRefresh = () => {
    backgroundSync.forceRefresh(queryClient);
  };

  const handleResetPerf = () => {
    cachePerformance.reset();
    setPerfStats(cachePerformance.getStats());
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="fixed bottom-4 right-4 z-50 bg-card border border-border/40 shadow-luxury-lg hover:bg-muted/50"
        >
          <Database className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Cache Manager
            <Badge variant="secondary" className="text-xs">DEV</Badge>
          </DialogTitle>
          <DialogDescription>
            Monitor and manage BahaMarket's intelligent caching system
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Cache Statistics */}
          {stats && (
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Database className="h-4 w-4" />
                Cache Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-blue-600">{stats.totalQueries}</div>
                  <div className="text-xs text-muted-foreground">Total Queries</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-green-600">{stats.freshQueries}</div>
                  <div className="text-xs text-muted-foreground">Fresh</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-yellow-600">{stats.staleQueries}</div>
                  <div className="text-xs text-muted-foreground">Stale</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-red-600">{stats.errorQueries}</div>
                  <div className="text-xs text-muted-foreground">Errors</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-purple-600">{stats.loadingQueries}</div>
                  <div className="text-xs text-muted-foreground">Loading</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-gray-600">
                    {(stats.cacheSize / 1024).toFixed(1)}KB
                  </div>
                  <div className="text-xs text-muted-foreground">Cache Size</div>
                </div>
              </div>
            </Card>
          )}

          {/* Performance Statistics */}
          {perfStats && (
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Performance Statistics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-green-600">{perfStats.hits}</div>
                  <div className="text-xs text-muted-foreground">Cache Hits</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-2xl font-bold text-red-600">{perfStats.misses}</div>
                  <div className="text-xs text-muted-foreground">Cache Misses</div>
                </div>
                <div className="col-span-2 text-center p-3 bg-muted/50 rounded">
                  <div className="text-3xl font-bold text-blue-600">{perfStats.hitRate}</div>
                  <div className="text-xs text-muted-foreground">Hit Rate</div>
                </div>
              </div>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={handleForceRefresh}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Force Refresh
              </Button>
              <Button
                variant="outline"
                onClick={handleClearAll}
                className="flex items-center gap-2 text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Clear All Cache
              </Button>
              <Button
                variant="outline"
                onClick={handleResetPerf}
                className="flex items-center gap-2"
              >
                <Clock className="h-4 w-4" />
                Reset Performance
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  cacheUtils.warmCache(queryClient);
                  setTimeout(() => {
                    setStats(cacheUtils.getCacheStats(queryClient));
                  }, 1000);
                }}
                className="flex items-center gap-2"
              >
                <Database className="h-4 w-4" />
                Warm Cache
              </Button>
            </div>
          </Card>

          {/* System Info */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              System Information
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Environment:</span>
                <Badge variant="secondary">Development</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cache Version:</span>
                <span className="font-mono">v1</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Background Sync:</span>
                <Badge variant="outline" className="text-green-600 border-green-200">Active</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Persistence:</span>
                <Badge variant="outline" className="text-blue-600 border-blue-200">localStorage</Badge>
              </div>
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CacheManager;